import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import './Quotation.css';
import { getQuotationWhatsAppLink, QUOTATION_LABEL } from '../../constants/quotation';
import WhatsAppButton from '../WhatsAppButton/WhatsAppButton';
import Menu from '../Menu/Menu';
import { logPageView, logEvent } from '../Analytics';
import DecryptedText from '../DecryptedText/DecryptedText';
import SpotlightCard from '../SpotlightCard/SpotlightCard';
import Threads from '../Threads/Threads';

const PASSWORD = 'Jamaik.2026';
const PDF_PATH = '/assets/docs/CotizacionTransformadoresJamaica.pdf';
const LINK_ISSUED_AT = new Date('2026-03-06T00:00:00Z');
const EXPIRATION_IN_MS = 7 * 24 * 60 * 60 * 1000;
const EXPIRATION_TIMESTAMP = LINK_ISSUED_AT.getTime() + EXPIRATION_IN_MS;
const EXPIRATION_DATE = new Date(EXPIRATION_TIMESTAMP);

const Quotation = () => {
  const [inputValue, setInputValue] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [errorKey, setErrorKey] = useState('');
  const { t, i18n } = useTranslation();
  const isExpired = Date.now() > EXPIRATION_TIMESTAMP;
  const expirationLabel = useMemo(() => {
    const locale = (i18n.language || 'es').split('-')[0];
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(EXPIRATION_DATE);
  }, [i18n.language]);
  const whatsappMessagePrefix = t('quotation.whatsappMessagePrefix');
  const whatsappLink = useMemo(() => getQuotationWhatsAppLink(whatsappMessagePrefix), [whatsappMessagePrefix]);
  const errorMessage = errorKey ? t(`quotation.${errorKey}`) : '';

  useEffect(() => {
    logPageView();
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isExpired) {
      setIsAuthorized(false);
      setErrorKey('errorExpired');
      logEvent({ action: 'password_attempt_expired', label: QUOTATION_LABEL });
      return;
    }
    if (inputValue.trim() === PASSWORD) {
      setIsAuthorized(true);
      setErrorKey('');
      logEvent({ action: 'password_unlock_success', label: QUOTATION_LABEL });
    } else {
      setIsAuthorized(false);
      setErrorKey('errorWrongPassword');
      logEvent({ action: 'password_unlock_failed', label: QUOTATION_LABEL });
    }
  };

  const handleDownloadClick = () => {
    logEvent({ action: 'pdf_download', label: QUOTATION_LABEL });
  };

  const handleWhatsAppRequestClick = () => {
    logEvent({ action: 'whatsapp_request_link', label: QUOTATION_LABEL });
  };

  return (
    <>
      <Menu />
      <div className="quotation-page">
        <Threads
          className="quotation-threads"
          amplitude={2.5}
          distance={0.5}
          enableMouseInteraction
        />
        <div className="quotation-page__content">
          <div className="quotation-hero">
            <p>
              <DecryptedText
                text={t('quotation.heroTitle')}
                animateOn="view"
                sequential
                revealDirection="start"
                parentClassName="quotation-hero__text"
                className="quotation-hero__char--revealed"
                encryptedClassName="quotation-hero__char--encrypted"
                characters="ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<>/\\"
              />
            </p>
          </div>

          <SpotlightCard className="quotation-card" spotlightColor="rgba(0, 229, 255, 0.25)">
            <h1>{t('quotation.title')}</h1>
            <p>
              <Trans
                i18nKey="quotation.intro"
                components={{
                  whatsapp: (
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleWhatsAppRequestClick}
                    />
                  ),
                }}
              />
            </p>

            <div className={`quotation-pill ${isExpired ? 'quotation-pill--expired' : ''}`}>
              {isExpired
                ? t('quotation.warningExpired', { date: expirationLabel })
                : t('quotation.warningActive', { date: expirationLabel })}
            </div>

            <form onSubmit={handleSubmit} className="quotation-form">
              <label htmlFor="quotation-password">{t('quotation.passwordLabel')}</label>
              <input
                id="quotation-password"
                type="password"
                placeholder={t('quotation.passwordPlaceholder')}
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                disabled={isAuthorized || isExpired}
              />
              <button type="submit" disabled={isAuthorized || isExpired}>
                {isAuthorized
                  ? t('quotation.buttonAuthorized')
                  : isExpired
                  ? t('quotation.buttonExpired')
                  : t('quotation.buttonUnlock')}
              </button>
            </form>

            {errorMessage && <p className="quotation-error">{errorMessage}</p>}
          </SpotlightCard>

          {isAuthorized && !isExpired && (
            <div className="quotation-viewer">
              <div className="quotation-viewer__header">
                <h2>{t('quotation.documentHeading')}</h2>
                <a
                  href={PDF_PATH}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="quotation-download"
                  onClick={handleDownloadClick}
                >
                  {t('quotation.download')}
                </a>
              </div>
              <object data={PDF_PATH} type="application/pdf" className="quotation-pdf">
                <p>
                  {t('quotation.fallbackText')}{' '}
                  <a
                    href={PDF_PATH}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleDownloadClick}
                  >
                    {t('quotation.fallbackLink')}
                  </a>.
                </p>
              </object>
            </div>
          )}
        </div>
      </div>
      <WhatsAppButton messagePrefix={whatsappMessagePrefix} />
    </>
  );
};

export default Quotation;
